import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import { capitalizeAllWords } from "domains/commons/misc";
import { useChatContext } from "domains/navigation/contexts/ChatProvider";
import useRouter from "domains/navigation/hooks/useRouter";
import { appShortcuts } from "domains/shortcuts/components/appShortcuts";
import { ShortcutsModal } from "domains/shortcuts/components/ShortcutsModal";
import { extraTheme } from "domains/theme";
import Avatar from "domains/ui/components/Avatar";
import Button, { ButtonProps } from "domains/ui/components/Button";
import DecisionModal from "domains/ui/components/DecisionModal";
import Icon from "domains/ui/components/Icon";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import {
  Divider,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StackProps,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useClerk } from "@clerk/nextjs";

export default function ProfileDropdown() {
  const router = useRouter();
  const { signOut } = useClerk();
  const { user } = useUserContext();
  const { isChatVisible, setIsChatVisible } = useChatContext();
  const [isSignOutConfirming, setIsSignOutConfirming] =
    useState<boolean>(false);
  const {
    isOpen: isShortcutsOpen,
    onOpen: onShortcutsOpen,
    onClose: onShortcutsClose,
    onToggle: onShortcutsToggle,
  } = useDisclosure();
  const changelogWidgetRef = useRef<typeof window.HellonextWidget>();

  const userEmail = user?.email ?? "-";
  const userName =
    user?.fullName ??
    capitalizeAllWords(
      (user?.email ?? "").replace(/@.+$/, "").replace(/\._-/g, " ")
    );

  // ----------------------------------

  const handleChangelogInit = useCallback(() => {
    if (!changelogWidgetRef.current) return;
    changelogWidgetRef.current.fallbackTriggerWrapperRef.style.width = 0;
    changelogWidgetRef.current.fallbackTriggerWrapperRef.style.height = 0;
    changelogWidgetRef.current.fallbackTriggerWrapperRef.style.right = 0;
    changelogWidgetRef.current.fallbackTriggerIFrameRef.contentWindow.document.getElementsByTagName(
      "html"
    )[0].style.colorScheme = "dark";
  }, []);

  const handleChangelogClick = useCallback(() => {
    if (!changelogWidgetRef.current) return;
    Track(AnalyticsEvents.Navigation.ClickedWhatsNew, {
      context: "profile-dropdown",
    });
    changelogWidgetRef.current.open();
  }, []);

  const handleSignOut = useCallback(() => {
    Track(AnalyticsEvents.Navigation.ClickedLogout);
    setIsSignOutConfirming(false);
    void signOut(() => {
      window.location.href = "/";
    });
  }, [setIsSignOutConfirming, signOut]);

  const handleContactClick = useCallback(() => {
    Track(AnalyticsEvents.Navigation.ClickedSupport, {
      context: "profile-dropdown",
    });
  }, []);

  const handleChatClick = useCallback(() => {
    if (isChatVisible) {
      setIsChatVisible(false);
      return;
    }
    setIsChatVisible(true);
    Track(AnalyticsEvents.Navigation.ClickedChat, {
      context: "profile-dropdown",
    });
  }, [isChatVisible, setIsChatVisible]);

  // ----------------------------------

  useHotkeys(
    appShortcuts.global.shortcuts.shortcutHelp.shortcut,
    onShortcutsToggle,
    { enabled: router.pathname !== "/canvas/[id]" && !router.query.openAssetId }
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!window.HellonextWidget || changelogWidgetRef.current) {
        return;
      }

      changelogWidgetRef.current = new window.HellonextWidget({
        modules: ["changelog"],
        type: "modal",
        openFrom: "center",
        theme: "dark",
        accent: {
          background: "#000000",
          primary: "#202020",
          button: "#0087CC",
        },
        token: "VKgUl5QrSsrVhpOs5s6VZA",
        postFilters: {
          bucket_id: [11_723, 11_796],
        },
        changelogFilters: {
          labels: [48_260, 48_261],
          per_page: 5,
        },
        customizations: {
          widgetWidth: "min(90%, 800px)",
        },
        autoOpenWhenNewChangelog: true,
        onInitialized: handleChangelogInit,
      });

      changelogWidgetRef.current.init();
    }, 1);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [handleChangelogInit]);

  // ----------------------------------

  return (
    <>
      <Popover
        openDelay={0}
        placement="bottom-end"
        trigger="hover"
        variant="blurred"
      >
        <PopoverTrigger>
          <Button
            w="auto"
            h="auto"
            p={0}
            colorScheme="white"
            data-testid="topbar-user-setting-dropdown-button"
            variant="alpha"
            data-group
            onClick={() => {
              Track(AnalyticsEvents.Navigation.ClickedProfile, {});
            }}
          >
            <HStack align="stretch" spacing={0}>
              <Avatar
                w="36px"
                h="36px"
                label={userName}
                borderRadius="md"
                size="md"
                transition={extraTheme.transitions.fast}
                _groupHover={{
                  filter: "brightness(1.2)",
                }}
                bgColor="backgroundSecondary.500"
                color="textSecondary"
              />
            </HStack>
          </Button>
        </PopoverTrigger>

        <PopoverContent w="220px">
          <PopoverBody>
            <VStack align="stretch" spacing={3}>
              <VStack align="start" spacing={1}>
                <Text w="100%" color="textSecondary" isTruncated size="body.md">
                  {userName}
                </Text>
                <Text w="100%" color="textSecondary" isTruncated size="body.sm">
                  {userEmail}
                </Text>
              </VStack>

              <ProfileMenuList>
                <ProfileMenuItem
                  leftIcon={<Icon id="Ui/Profile" h="12px" />}
                  internalLink="/profile"
                  data-testid="user-menu-profile-button"
                >
                  Profile
                </ProfileMenuItem>

                <ProfileMenuItem
                  leftIcon={<Icon id="Layout/SignOut" h="12px" />}
                  data-testid="user-menu-logout-button"
                  onClick={() => setIsSignOutConfirming(true)}
                >
                  Logout
                </ProfileMenuItem>
              </ProfileMenuList>

              <ProfileMenuList>
                <ProfileMenuItem
                  leftIcon={<Icon id="Ui/Paperplane" h="12px" />}
                  externalLink="https://docs.google.com/forms/d/e/1FAIpQLScTW4rxKfm5XgMX0WyjP6dXin1qrrsDEUN8EL9BfG7WNx1Qig/viewform?usp=share_link"
                  onClick={handleContactClick}
                >
                  Contact Us
                </ProfileMenuItem>

                <ProfileMenuItem
                  leftIcon={<Icon id="Ui/Bubble" h="12px" />}
                  onClick={handleChatClick}
                >
                  Chat Support
                </ProfileMenuItem>

                <ProfileMenuItem
                  leftIcon={<Icon id="Ui/Keyboard" h="10px" />}
                  onClick={() => {
                    Track(AnalyticsEvents.Navigation.ClickedShortcuts, {
                      context: "profile-dropdown",
                    });
                    onShortcutsOpen();
                  }}
                >
                  Keyboard Shortcuts
                </ProfileMenuItem>
              </ProfileMenuList>

              <ProfileMenuList>
                <ProfileMenuItem
                  tooltip="Discover our recent additions and updates"
                  onClick={handleChangelogClick}
                >
                  {"What's New?"}
                </ProfileMenuItem>

                <ProfileMenuItem
                  tooltip="Explore our comprehensive Knowledge Center"
                  externalLink="https://help.scenario.com/"
                  onClick={() =>
                    Track(AnalyticsEvents.Navigation.ClickedHelpCenter, {
                      context: "profile-dropdown",
                    })
                  }
                >
                  Knowledge Center
                </ProfileMenuItem>

                <ProfileMenuItem
                  tooltip="Explore how we safeguard your data"
                  externalLink="https://trust.scenario.com/"
                  onClick={() =>
                    Track(AnalyticsEvents.Navigation.ClickedTrustCenter, {
                      context: "profile-dropdown",
                    })
                  }
                >
                  Trust Center
                </ProfileMenuItem>

                <ProfileMenuItem
                  tooltip="Join our Discord for networking and support"
                  externalLink="https://discord.gg/scenario-ai-generated-game-assets-1042738264639012864"
                >
                  Community Hub
                </ProfileMenuItem>

                <ProfileMenuItem
                  tooltip="Request or upvote upcoming features"
                  externalLink="https://feedback.scenario.com/"
                  onClick={() => {
                    Track(AnalyticsEvents.Navigation.ClickedFeedback, {
                      context: "profile-dropdown",
                    });
                  }}
                >
                  {"Feedback & Suggestions"}
                </ProfileMenuItem>

                <ProfileMenuItem
                  tooltip="Earn 20% on paid referrals"
                  externalLink="https://affiliates.scenario.com"
                  onClick={() => {
                    Track(AnalyticsEvents.Navigation.ClickedReferral, {
                      context: "profile-dropdown",
                    });
                  }}
                >
                  Refer a friend
                </ProfileMenuItem>
              </ProfileMenuList>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <DecisionModal
        isOpen={isSignOutConfirming}
        headerMessage="Confirm Logout"
        body="Are you sure you want to log out?"
        onConfirm={handleSignOut}
        onClose={() => setIsSignOutConfirming(false)}
        colorScheme="danger"
        confirmMessage="Logout"
      />

      <ShortcutsModal
        isOpen={isShortcutsOpen}
        onClose={onShortcutsClose}
        shortcuts={appShortcuts}
      />
    </>
  );
}

// ------------------------------------

type ProfileMenuListProps = StackProps;

function ProfileMenuList(props: ProfileMenuListProps) {
  return (
    <>
      <Divider borderColor="borderAlpha.500" />

      <VStack align="stretch" mx={-2} my={-1} spacing={1} {...props} />
    </>
  );
}

// ------------------------------------

type ProfileMenuItemProps = ButtonProps;

function ProfileMenuItem(props: ProfileMenuItemProps) {
  return (
    <Button
      variant="menuItem"
      h={8}
      colorScheme="white"
      {...props}
      tooltipProps={{ placement: "left", ...props.tooltipProps }}
    />
  );
}
